import Image from 'next/image'
import { useMemo, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'src/utils/i18n'
import { messageInfo } from '../Message'
import Tutorial from './Tutorial'
import PhoneCarousel from './PhoneCarousel'
import Link from 'next/link'
import { getCode } from 'src/@api/android'
import { CheckCode } from 'src/@api/android'
import { message } from 'antd'
import { Carousel } from 'antd'
import Icon from 'src/@core/components/icon'
import { useRouter } from 'next/router'
import resolveCurrentOrderId, { clearBindOrderContext } from 'src/utils/resolveCurrentOrderId'
import { applyBoundDeviceByOrderId } from 'src/utils/appDeviceBridge'

const App1 = (data) => {
  const router = useRouter()
  const [useRegistrationCode, setRegistrationCode] = useState('')

  useEffect(() => {
    let active = true

    const getRegistrationCode = async () => {
      try {
        const orderId = await resolveCurrentOrderId()
        const result = orderId ? await getCode({ params: { id: orderId } }) : await getCode()
        if (!active) return

        sessionStorage.setItem('androidRegistrationCode', result.data)
        setRegistrationCode(result.data)
      } catch (err) {
        console.error('获取 Android 注册码失败', err)
      }
    }

    getRegistrationCode()

    return () => {
      active = false
    }
  }, [router.asPath])

  const { t } = useTranslation('commonAndroid')

  const step = [
    {
      title: t("Enter the registration code above in Google Account Verification"),
      img: '/images/android/icloud/step4.webp'
    },
    {
      title: t('Sign in with the Google account used on the target device'),
      img: '/images/android/icloud/step5.webp'
    },
    {
      title: t("Enter the password"),
      img: '/images/android/icloud/step6.webp'
    },
    {
      title: t("A number will be shown on your phone At the same time the target phone will receive a Google notification"),
      img: '/images/android/icloud/step7.webp'
    },
    {
      title: t('Tap Yes its me on the target phone'),
      img: '/images/android/icloud/step8.webp'
    },
    {
      title: t("Tap the matching number on the target phone"),
      img: '/images/android/icloud/step9.webp'
    },
    {
      title: t("Back to your own phone tap I agree"),
      img: '/images/android/icloud/step10.webp'
    },
    {
      title: t('Wait for about 5 seconds'),
      img: '/images/android/icloud/step11.webp'
    }
  ]

  const [autoplay, setAutoplay] = useState(true)

  useEffect(() => {
    const handleClick = event => {
      if (window.innerWidth < 600) {
        setAutoplay(false)

        setTimeout(() => {
          setAutoplay(true)
        }, [5000])
      }
    }

    // 添加点击事件监听器
    document.addEventListener('click', handleClick)

    // 添加滑动事件监听器
    document.addEventListener('scroll', handleClick)

    // 在组件卸载时移除事件监听器
    return () => {
      document.removeEventListener('click', handleClick)
      document.removeEventListener('scroll', handleClick)
    }
  }, [autoplay]) // 空数组表示只在组件加载和卸载时运行

  const [currentSlide, setcurrentSlide] = useState(0)

  const onChange = currentSlide1 => {
    setcurrentSlide(currentSlide1)
  }

  const startMonitoring = async () => {
    const androidRegistrationCode = sessionStorage.getItem('androidRegistrationCode')
    const res = await CheckCode({ code: androidRegistrationCode })

    if (res.code != 0) {
      message.warning('Verification failed')

      return
    }

    // 注册码校验通过后：延迟选中刚绑设备，并刷新侧栏 Package 列表；成功则清未绑上下文再进 Dashboard
    const orderId = await resolveCurrentOrderId()
    const selected = await applyBoundDeviceByOrderId({
      orderId,
      platformType: 'Android-Cloud'
    })

    if (selected.ok) {
      clearBindOrderContext()
      router.replace(selected.dashboardPath)

      return
    }

    // 选中失败：侧栏已在 applyBoundDeviceByOrderId finally 里刷过；仍跳 Dashboard（兼容列表延迟）
    router.replace('/android/dashboard')
  }

  const imgToggle = useRef()

  const Handler = index => {
    if (index !== 8) {
      imgToggle.current.goTo(index)
    } else {
      imgToggle.current.goTo(0)
    }
  }

  const StepProgress = ({ current = 2, total = 2 }) => {
    const barWidth = 72 // 宽度

    return (
      <div className="flex items-center justify-center gap-4 mb-[40px] Mlg:mb-[20px]">
        <div className="text-[20px] leading-[30px] font-medium text-[#484B60] Mlg:text-[16px] Mlg:leading-[24px] ">
          {current}/{total}
        </div>

        <div className="flex gap-x-[6px]">
          {Array.from({ length: total }).map((_, i) => {
            const active = i < current;

            return (
              <div
                key={i}
                className="h-[4px] w-[72px] rounded-[1px]"
                style={
                  active
                    ? {
                      background: "linear-gradient(90deg, #09A6EA 1.56%, #6838F2 47.99%)",
                      backgroundSize: `${current * barWidth}px 4px`,
                      backgroundPosition: `-${i * barWidth}px 0px`,
                    }
                    : {
                      background: "#E8EDFF",
                    }
                }
              />
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className='w-full flex flex-col items-center smj:pb-10'>
      {data.fullScreen && (
        <Icon onClick={() => { data.setFullScreen(false) }}
          icon='maki:cross'
          className={`absolute top-2 right-3 w-[20px] h-[20px] text-black/30`}
        />
      )}
      <StepProgress />
      <div className={`max-w-[722px] flex justify-between items-center ${data.fullScreen && 'flex-col'}`}>
        <div className={`max-w-[450px] ${data.fullScreen && 'order-2 pb-5'}`}>
          <p className='text-[24px] Dsm:text-[20px] text-[#2B2D33] mx-auto font-[600] Dsm:text-center'>
            {t('Enter the Google account and password of the target Android')}
          </p>
          <div className='flex flex-col items-center mt-[32px] Dsm:mt-[24px]'>
            <p className='text-center font-[600] text-[#454CFF] text-[42px] Dsm:text-[36px]'>{useRegistrationCode}</p>
          </div>
          <div
            onMouseEnter={() => {
              if (window.innerWidth > 600) {
                setAutoplay(false)
              }
            }}
            onMouseLeave={() => {
              if (window.innerWidth > 600) {
                setAutoplay(true)
              }
            }}
            className={`${data.fullScreen ? '' : 'Dsm:w-[96%] mx-auto'}`}
          >
            {step.map((item, index) => {
              return (
                <div key={index}>
                  <div onClick={() => Handler(index)}
                    className={`flex text-[14px] cursor-pointer text-black mt-[10px]`}>
                    <p className={`w-[24px] h-[22px] rounded-full flex items-center justify-center mt-[2px] ${currentSlide === index && 'bg-[#2A99FF] text-white'}`}>
                      {index + 1}.
                    </p>
                    <div className={`ml-1 w-[100%] mt-[2px] text-[15px]`}
                      dangerouslySetInnerHTML={{ __html: item.title }}
                    />
                  </div>
                  {/* ⛔ Find it here 已隐藏：跳 /guide（msafely 没有该文章页）2026-09-21。恢复：删掉 false && */}
                  {false && index === 1 &&
                    <div className='mt-[10px] w-full flex justify-between h-[40px] text-[13px] text-white items-center px-[10px] rounded-[8px] bg-[linear-gradient(270deg,_#06BAFD_-0.22%,_#6B51FE_84.27%)]'>
                      <p>{t('Not sure which account to use')}?</p>
                      <div className='flex items-center'>
                        <Image src={'/images/android/setup/user.svg'} width={18} height={18} alt='user' />
                        <Link className='underline ml-[6px]' target="_blank" rel="noopener noreferrer"
                          href='/guide/user-guide-android'>
                          {t('Find it here')}
                        </Link>
                      </div>
                    </div>}
                </div>
              )
            })}
          </div>
        </div>
        <div
          className={`w-[222px] ml-[50px] Dsm:m-0 h-auto relative ${data.fullScreen ? 'order-1 mx-auto pb-5' : 'Ssm:hidden'
            }`}
        >
          <Carousel
            waitForAnimate={true}
            autoplay={autoplay}
            className='flex overflow-hidden w-[227px] h-auto'
            ref={imgToggle}
            cancelable={false}
            afterChange={onChange}
          >
            {step.map((item, index) => {
              return (
                <div key={index} className=''>
                  <Image
                    alt='img'
                    width={100}
                    height={100}
                    src={item.img}
                    className='mx-auto w-[207px] h-[447px] rounded-[15px]'
                  />
                </div>
              )
            })}
          </Carousel>
          <div className='flex items-center justify-center mt-6'>
            {step.map((item, index) => {
              return (
                <div
                  onClick={() => Handler(index)}
                  key={index}
                  className={`border-[1px] ${index !== 0 && 'ml-2'
                    } border-[#2A99FF] w-[12px] h-[12px] cursor-pointer rounded-[15px] ${currentSlide === index && 'bg-[#2A99FF]'
                    } `}
                ></div>
              )
            })}
          </div>
        </div>
      </div>
      <div className={`flex flex-col items-center Smd:hidden ${data.fullScreen && 'hidden'} mb-4`} onClick={() => data.setFullScreen(true)}>
        <Image
          src='/images/bind/watch.svg'
          width={100}
          height={100}
          alt='img'
          className='mx-auto w-[58px] mt-[36px]'
        />
        <p className='mt-[14px] text-[14px] text-[#454CFF] font-[600]'>{t('Watch a tutorial')}</p>
      </div>

      <div className='w-full mx-auto fixed bottom-0 bg-white border-t-[1px] border-[#E5E7EB]'>
        <div className='flex justify-center w-full p-5 Mlg:px-0 bg-white mx-auto'>
          <div className='w-full lg:w-[620px] flex justify-between smj:px-4'>
            <button onClick={() => { data.setCurrentItem('BackupTips') }}
              className='border-[1px] border-[#454CFF] bg-white rounded-[24px] h-[35px] flex items-center pl-[10px] pr-[19px]'>
              <Image className='mr-[7px]' src='/images/android/setup/back.svg' width={18} height={18} alt='back' />
              <span className='text-[15px] text-[#454CFF] font-[600]'>{t('BACK')}</span>
            </button>

            <button
              onClick={startMonitoring}
              className={`border-0 bg-[#FFCF55] rounded-[41px] h-[35px] text-[15px] flex items-center pl-[24px] pr-[10px]`}
            >
              <span className='text-[15px] text-[#1B1C2B] font-[600]'>{t('Start monitoring')}</span>
              <Image className='ml-[6px]' src='/images/android/setup/right.svg' width={18} height={18} alt='right' />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default App1
